import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQuery } from "@/api/base-query";
import { API_ENDPOINTS } from "@/api/config";
import {
  showErrorNotification,
} from "@/store/common/commonSlice";
import type {
  CompleteUploadSessionRequest,
  CompleteUploadSessionResponse,
  ContentApiResponse,
  CreateUploadSessionRequest,
  CreateUploadSessionResponse,
  DeleteFileUsageRequest,
  DeleteFileUsageResponse,
  GetFileUsageRequest,
  GetFileUsageResponse,
  ListFileUsagesRequest,
  ListFileUsagesResponse,
  ResolveFileReferenceRequest,
  ResolveFileReferenceResponse,
  UpsertFileUsageRequest,
  UpsertFileUsageResponse,
} from "./uploads.types";

const contentBaseQuery = createBaseQuery("contentApi");
const CONTENT_ENDPOINTS = API_ENDPOINTS.content;

type ApiDispatcher = { dispatch: (action: unknown) => void };

const createApiError = (status: number | string, message: string) => ({
  error: {
    status,
    data: { message },
  },
});

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === "object") {
    const data = (error as { data?: { message?: unknown } }).data;
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message;
    }
  }
  return fallback;
};

const getResponseErrorMessage = <T>(
  response: ContentApiResponse<T> | undefined,
  fallback: string,
): string | null => {
  if (response?.success) return null;
  return response?.message || fallback;
};

const notifyError = (api: ApiDispatcher, message: string) => {
  api.dispatch(
    showErrorNotification({
      title: "Error",
      message,
    }),
  );
};

const notifyResultError = (
  api: ApiDispatcher,
  error: unknown,
  fallback: string,
) => {
  notifyError(api, getErrorMessage(error, fallback));
};

const validateCreateSessionRequest = (
  input: CreateUploadSessionRequest,
): string | null => {
  if (!input.expectedFilename.trim()) return "File name is required.";
  if (!input.expectedMimeType.trim()) return "File MIME type is required.";
  if (!Number.isInteger(input.expectedSizeBytes) || input.expectedSizeBytes <= 0) {
    return "File size must be a positive byte count.";
  }
  if (!/^[a-f0-9]{64}$/.test(input.expectedSha256Hex)) {
    return "File checksum must be a lowercase SHA-256 hex digest.";
  }
  if (!input.idempotencyKey.trim()) return "Upload idempotency key is required.";

  return null;
};

export const contentUploadsApi = createApi({
  reducerPath: "contentUploadsApi",
  baseQuery: contentBaseQuery,
  keepUnusedDataFor: 0,
  refetchOnReconnect: true,
  refetchOnFocus: false,
  endpoints: (builder) => ({
    createUploadSession: builder.mutation<
      CreateUploadSessionResponse,
      CreateUploadSessionRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const validationError = validateCreateSessionRequest(input);
        if (validationError) return createApiError(400, validationError);

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.uploadSessions,
          method: "POST",
          body: {
            expectedFilename: input.expectedFilename,
            expectedMimeType: input.expectedMimeType,
            expectedSizeBytes: input.expectedSizeBytes,
            expectedSha256Hex: input.expectedSha256Hex,
            idempotencyKey: input.idempotencyKey,
            ...(input.folderKey?.trim()
              ? { folderKey: input.folderKey.trim() }
              : {}),
          },
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to create upload session.");
          return { error: result.error };
        }

        const response = result.data as CreateUploadSessionResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to create upload session.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        return { data: response };
      },
    }),

    completeUploadSession: builder.mutation<
      CompleteUploadSessionResponse,
      CompleteUploadSessionRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const uploadKey = input.uploadKey.trim();
        if (!uploadKey) return createApiError(400, "Upload key is required.");

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.completeUploadSession(uploadKey),
          method: "POST",
          body: {
            mediaType: input.mediaType,
            sourceType: input.sourceType,
            visibility: input.visibility,
          },
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to finalize upload.");
          return { error: result.error };
        }

        const response = result.data as CompleteUploadSessionResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to finalize upload.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }
        return { data: response };
      },
    }),

    resolveFileReference: builder.mutation<
      ResolveFileReferenceResponse,
      ResolveFileReferenceRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const url = input.url.trim();
        if (!url) return createApiError(400, "File URL is required.");

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.resolveFileReference,
          method: "POST",
          body: { url },
        });

        if (result.error) {
          if (!input.suppressNotifications) {
            notifyResultError(
              api,
              result.error,
              "Failed to resolve file reference.",
            );
          }
          return { error: result.error };
        }

        const response = result.data as ResolveFileReferenceResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to resolve file reference.",
        );
        if (responseError) {
          if (!input.suppressNotifications) {
            notifyError(api, responseError);
          }
          return createApiError(400, responseError);
        }

        return { data: response };
      },
    }),

    upsertFileUsage: builder.mutation<
      UpsertFileUsageResponse,
      UpsertFileUsageRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const fileKey = input.fileKey.trim();
        if (!fileKey) return createApiError(400, "File key is required.");

        const sourceVersionKey = input.sourceVersionKey.trim();
        const usedInEntityType = input.usedInEntityType.trim();
        const usedInEntityKey = input.usedInEntityKey.trim();
        const usedInEntityName = input.usedInEntityName.trim();
        const usageContext = input.usageContext.trim();

        if (!sourceVersionKey) {
          return createApiError(400, "Source version key is required.");
        }
        if (!usedInEntityType) {
          return createApiError(400, "Used-in entity type is required.");
        }
        if (
          !Number.isInteger(input.usedInEntityId) ||
          input.usedInEntityId <= 0
        ) {
          return createApiError(400, "Used-in entity ID is required.");
        }
        if (!usedInEntityKey) {
          return createApiError(400, "Used-in entity key is required.");
        }
        if (!usedInEntityName) {
          return createApiError(400, "Used-in entity name is required.");
        }
        if (!usageContext) {
          return createApiError(400, "Usage context is required.");
        }

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.upsertFileUsage(fileKey),
          method: "PUT",
          body: {
            sourceVersionKey,
            usedInEntityType,
            usedInEntityId: input.usedInEntityId,
            usedInEntityKey,
            usedInEntityName,
            usageContext,
          },
        });

        if (result.error) {
          if (!input.suppressNotifications) {
            notifyResultError(api, result.error, "Failed to update file usage.");
          }
          return { error: result.error };
        }

        const response = result.data as UpsertFileUsageResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to update file usage.",
        );
        if (responseError) {
          if (!input.suppressNotifications) {
            notifyError(api, responseError);
          }
          return createApiError(400, responseError);
        }

        return { data: response };
      },
    }),

    getFileUsage: builder.query<GetFileUsageResponse, GetFileUsageRequest>({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const fileKey = input.fileKey.trim();
        if (!fileKey) return createApiError(400, "File key is required.");

        const usedInEntityType = input.usedInEntityType.trim();
        const usageContext = input.usageContext.trim();

        if (!usedInEntityType) {
          return createApiError(400, "Used-in entity type is required.");
        }
        if (
          !Number.isInteger(input.usedInEntityId) ||
          input.usedInEntityId <= 0
        ) {
          return createApiError(400, "Used-in entity ID is required.");
        }
        if (!usageContext) {
          return createApiError(400, "Usage context is required.");
        }

        const params = new URLSearchParams({
          usedInEntityType,
          usedInEntityId: String(input.usedInEntityId),
          usageContext,
        });

        const result = await baseQuery({
          url: `${CONTENT_ENDPOINTS.upsertFileUsage(fileKey)}?${params.toString()}`,
          method: "GET",
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to fetch file usage.");
          return { error: result.error };
        }

        const response = result.data as GetFileUsageResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to fetch file usage.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        return { data: response };
      },
    }),

    listFileUsages: builder.query<
      ListFileUsagesResponse,
      ListFileUsagesRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const fileKey = input.fileKey.trim();
        if (!fileKey) return createApiError(400, "File key is required.");

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.upsertFileUsage(fileKey),
          method: "GET",
        });

        if (result.error) {
          notifyResultError(api, result.error, "Failed to fetch file usages.");
          return { error: result.error };
        }

        const response = result.data as ListFileUsagesResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to fetch file usages.",
        );
        if (responseError) {
          notifyError(api, responseError);
          return createApiError(400, responseError);
        }

        return { data: response };
      },
    }),

    deleteFileUsage: builder.mutation<
      DeleteFileUsageResponse,
      DeleteFileUsageRequest
    >({
      queryFn: async (input, api, _extraOptions, baseQuery) => {
        const fileKey = input.fileKey.trim();
        if (!fileKey) return createApiError(400, "File key is required.");

        const usedInEntityType = input.usedInEntityType.trim();
        const usageContext = input.usageContext.trim();

        if (!usedInEntityType) {
          return createApiError(400, "Used-in entity type is required.");
        }
        if (
          !Number.isInteger(input.usedInEntityId) ||
          input.usedInEntityId <= 0
        ) {
          return createApiError(400, "Used-in entity ID is required.");
        }
        if (!usageContext) {
          return createApiError(400, "Usage context is required.");
        }

        const result = await baseQuery({
          url: CONTENT_ENDPOINTS.upsertFileUsage(fileKey),
          method: "DELETE",
          body: {
            usedInEntityType,
            usedInEntityId: input.usedInEntityId,
            usageContext,
          },
        });

        if (result.error) {
          if (!input.suppressNotifications) {
            notifyResultError(api, result.error, "Failed to delete file usage.");
          }
          return { error: result.error };
        }

        const response = result.data as DeleteFileUsageResponse;
        const responseError = getResponseErrorMessage(
          response,
          "Failed to delete file usage.",
        );
        if (responseError) {
          if (!input.suppressNotifications) {
            notifyError(api, responseError);
          }
          return createApiError(400, responseError);
        }

        return { data: response };
      },
    }),
  }),
});

export const {
  useCreateUploadSessionMutation,
  useCompleteUploadSessionMutation,
  useResolveFileReferenceMutation,
  useUpsertFileUsageMutation,
  useGetFileUsageQuery,
  useLazyGetFileUsageQuery,
  useListFileUsagesQuery,
  useLazyListFileUsagesQuery,
  useDeleteFileUsageMutation,
} = contentUploadsApi;
